import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Building2, Plus, Search, Download, RefreshCw,
  MoreHorizontal, Eye, Pencil, Trash2,
} from 'lucide-react'
import {
  useLaboratoriosList,
  useLaboratoriosStats,
  useEliminarLaboratorioCompleto,
  useExportarLaboratorios,
  usePaises,
  useTiposLaboratorioFull,
} from '../hooks/useBdClientes'
import { StatsCard } from '../components/StatsCard'
import type { FiltrosLaboratorios } from '../types'

function activoBadge(activo: boolean) {
  if (activo) {
    return <Badge variant="outline" className="badge-gradient text-green-700 dark:text-green-500 border-green-500">Activo</Badge>
  }
  return <Badge variant="outline" className="text-gray-500 border-gray-400">Inactivo</Badge>
}

export function LaboratoriosListPage() {
  useDocumentTitle('Laboratorios - Seguimiento')
  const navigate = useNavigate()

  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [filtros, setFiltros] = useState<FiltrosLaboratorios>({ page: 1, page_size: 25 })

  const { data, isLoading } = useLaboratoriosList(filtros)
  const { data: stats, isLoading: statsLoading } = useLaboratoriosStats()
  const eliminarMutation = useEliminarLaboratorioCompleto()
  const exportarMutation = useExportarLaboratorios()

  const { data: paises } = usePaises(true)
  const { data: tipos } = useTiposLaboratorioFull(true)

  const handleSearch = () => {
    setFiltros(prev => ({ ...prev, search: searchInput || undefined, page: 1 }))
    setPage(1)
  }

  const handleFilterChange = (key: string, value: string) => {
    setFiltros(prev => ({
      ...prev,
      [key]: value === 'all' ? undefined : (key === 'activo' ? value === 'true' : Number(value)),
      page: 1,
    }))
    setPage(1)
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
    setFiltros(prev => ({ ...prev, page: newPage }))
  }

  const handleDelete = async (id: number) => {
    if (confirm('¿Estás seguro de eliminar este laboratorio? Esta acción no se puede deshacer.')) {
      await eliminarMutation.mutateAsync(id)
    }
  }

  const handleExport = () => {
    exportarMutation.mutate(filtros)
  }

  const clearFilters = () => {
    setSearchInput('')
    setFiltros({ page: 1, page_size: 25 })
    setPage(1)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Building2 className="h-6 w-6 text-black dark:text-white" />
          Laboratorios - Seguimiento
        </h1>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exportarMutation.isPending}
            className="border-gray-700 text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <Download className="mr-2 h-4 w-4" />
            Exportar
          </Button>
          <Button
            size="sm"
            onClick={() => navigate('/bd-clientes/laboratorios/crear')}
            className="bg-teal-800 hover:bg-teal-900 text-white"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Laboratorio
          </Button>
        </div>
      </div>

      {/* Stats */}
      {statsLoading ? (
        <div className="grid gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <StatsCard title="Total" value={stats.total} icon={Building2} />
          <StatsCard title="Activos" value={stats.activos} icon={Building2} />
          <StatsCard title="Inactivos" value={stats.inactivos} icon={Building2} />
          <StatsCard title="Países" value={stats.total_paises} icon={Building2} />
        </div>
      )}

      {/* Filtros */}
      <Card className="p-4 border border-gray-300 dark:border-gray-700">
        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                placeholder="Buscar por razón social, identificación, correo..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button variant="outline" onClick={handleSearch}>
              <Search className="h-4 w-4" />
            </Button>
            <Button variant="ghost" onClick={clearFilters} title="Limpiar filtros">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <Select
              value={filtros.pais_id?.toString() || 'all'}
              onValueChange={(v) => handleFilterChange('pais_id', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="País" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los países</SelectItem>
                {paises?.map((p) => (
                  <SelectItem key={p.id} value={p.id.toString()}>{p.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filtros.tipo_id?.toString() || 'all'}
              onValueChange={(v) => handleFilterChange('tipo_id', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Tipo Laboratorio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {tipos?.map((t) => (
                  <SelectItem key={t.id} value={t.id.toString()}>{t.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filtros.activo !== undefined ? filtros.activo.toString() : 'all'}
              onValueChange={(v) => handleFilterChange('activo', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="true">Activo</SelectItem>
                <SelectItem value="false">Inactivo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Tabla */}
      <Card className="border border-gray-300 dark:border-gray-700">
        {isLoading ? (
          <div className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                  <TableHead>Razón Social</TableHead>
                  <TableHead>Identificación</TableHead>
                  <TableHead>País</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Grupo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="w-[70px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.results && data.results.length > 0 ? (
                  data.results.map((lab) => (
                    <TableRow
                      key={lab.id}
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-[hsl(220,15%,16%)]"
                    >
                      <TableCell
                        className="font-medium text-black dark:text-white"
                        onClick={() => navigate(`/bd-clientes/laboratorios/${lab.id}`)}
                      >
                        {lab.razon_social}
                      </TableCell>
                      <TableCell>{lab.identificacion}</TableCell>
                      <TableCell>{lab.pais_nombre}</TableCell>
                      <TableCell>{lab.tipo_nombre}</TableCell>
                      <TableCell>{lab.grupo_nombre}</TableCell>
                      <TableCell>{activoBadge(lab.activo)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(lab.fecha_creacion).toLocaleDateString('es-EC')}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/bd-clientes/laboratorios/${lab.id}`)}>
                              <Eye className="mr-2 h-4 w-4" />
                              Ver detalle
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/bd-clientes/laboratorios/${lab.id}/editar`)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => handleDelete(lab.id)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No se encontraron laboratorios
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Paginación */}
            {data && data.total_pages > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-gray-300 dark:border-gray-700">
                <p className="text-sm text-muted-foreground">
                  {data.count} laboratorios encontrados
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                  >
                    Anterior
                  </Button>
                  <span className="flex items-center text-sm px-2">
                    Página {page} de {data.total_pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === data.total_pages}
                    onClick={() => handlePageChange(page + 1)}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  )
}
